import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { canonicalJson, historyDays, parsePolicySpec, policyCompleteness, STANDARD_PLAN_SPEC, type PolicySpec } from '@zerogap/shared';
import { del, get, patch, post, put } from '../api';
import { arrow, tr } from '../i18n';
import { useTenant } from '../session';
import { computerStatus } from '../status';
import { Badge, Button, Card, ConfirmDialog, Empty, ErrorBox, Field, friendlyError, HelpTip, Loading, Modal, PageHead, TechDetails, useToast, when } from '../ui';
import { AdvancedSection, KeepSection, PlanLines, shortensHistory, spanText, suggestedName, WhatSection, WhenSection } from './plan-form';

/**
 * Backup plans (called "policies" in the API). Every computer follows exactly one plan; the default plan
 * is simply the one newly connected computers get.
 */

export interface PlanRow {
  id: string;
  name: string;
  spec: PolicySpec;
  complete: boolean;
  paused: boolean;
  is_default: boolean;
  effective_device_count: number;
  updated_at: string;
}

interface DeviceRow {
  id: string;
  display_name: string;
  status: string;
  policy_id: string | null;
  policy_name: string | null;
  protection: { online: boolean; protected: boolean; reason: string };
  last_success_at: string | null;
  last_seen_at: string | null;
  last_job_state: string | null;
}

export const usePlans = () => {
  const t = useTenant();
  return useQuery({ queryKey: ['policies', t.tenantId], queryFn: () => get<{ policies: PlanRow[] }>(`/api/t/${t.tenantId}/policies`) });
};

const useComputers = () => {
  const t = useTenant();
  return useQuery({
    queryKey: ['devices-all', t.tenantId],
    queryFn: () => get<{ devices: DeviceRow[] }>(`/api/t/${t.tenantId}/devices?limit=200`),
    select: (d) => d.devices.filter((x) => x.status !== 'retired'),
  });
};

const usedBy = (n: number) => (n === 1 ? tr('Used by 1 computer') : tr('Used by {n} computers', { n }));

function invalidatePlans(qc: ReturnType<typeof useQueryClient>) {
  for (const k of ['policies', 'policy', 'devices', 'devices-all', 'device', 'dashboard', 'plan-devices']) qc.invalidateQueries({ queryKey: [k] });
}

// ───────── list ─────────

export function Plans() {
  const t = useTenant();
  const nav = useNavigate();
  const list = usePlans();
  const plans = list.data?.policies ?? [];
  const hasDefault = plans.some((p) => p.is_default);
  return (
    <div>
      <PageHead
        title={tr('Backup plans')}
        desc={tr('A backup plan decides what gets backed up, when, and how long backups are kept. Every computer follows one plan.')}
        actions={t.can('policies.manage') ? <Link className="btn primary" to={`${t.base}/plans/new`}>{tr('Create a backup plan')}</Link> : undefined}
      />
      {list.isLoading ? <Loading /> : list.error ? <ErrorBox error={list.error} retry={list.refetch} /> : (
        <>
          {!hasDefault && plans.length > 0 && t.can('policies.manage') && <NoDefault plans={plans} />}
          {plans.length === 0 ? (
            <Card><Empty glyph="✧" title={tr('No backup plans yet')}>{tr('Create a plan to choose what gets backed up.')}</Empty></Card>
          ) : (
            <div className="grid g2">
              {plans.map((p) => (
                <button key={p.id} type="button" className="card plan-card" onClick={() => nav(`${t.base}/plans/${p.id}`)}>
                  <div className="row between" style={{ alignItems: 'flex-start' }}>
                    <h3>{p.name}</h3>
                    <div className="row" style={{ gap: 6 }}>
                      {p.is_default && <span className="chip">{tr('Default')}</span>}
                      {!p.complete ? <Badge tone="warn">{tr('Not finished')}</Badge> : p.paused ? <Badge tone="warn">{tr('Paused')}</Badge> : null}
                    </div>
                  </div>
                  <PlanLines spec={p.spec} />
                  <div className="small muted">
                    {usedBy(p.effective_device_count)}
                    {p.is_default && <> · {tr('New computers get this plan')}</>}
                  </div>
                </button>
              ))}
            </div>
          )}
          <p className="small muted" style={{ marginTop: 16 }}>
            {tr('The default plan is the one new computers get when they connect. You can move any computer to another plan at any time.')}
          </p>
        </>
      )}
    </div>
  );
}

function NoDefault({ plans }: { plans: PlanRow[] }) {
  const t = useTenant();
  const qc = useQueryClient();
  const toast = useToast();
  const ready = plans.filter((p) => p.complete);
  const [id, setId] = useState(ready[0]?.id ?? '');
  if (!ready.length) return null;
  return (
    <div className="notice warn" style={{ marginBottom: 16 }}>
      <strong>{tr('Choose the plan new computers should get')}</strong>
      <div className="row" style={{ marginTop: 8 }}>
        <select value={id} onChange={(e) => setId(e.target.value)} style={{ maxWidth: 280 }}>{ready.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
        <Button size="sm" onClick={async () => {
          try {
            await post(`/api/t/${t.tenantId}/policies/${id}/set-default`);
            invalidatePlans(qc);
          } catch (e) {
            toast(friendlyError(e), true);
          }
        }}>{tr('Make it the default')}</Button>
      </div>
    </div>
  );
}

// ───────── create ─────────

export function PlanWizard() {
  const t = useTenant();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const plans = usePlans();
  const computers = useComputers();
  const [step, setStep] = useState(0);
  const [spec, setSpec] = useState<PolicySpec>(() => parsePolicySpec(STANDARD_PLAN_SPEC));
  const [name, setName] = useState('');
  const [named, setNamed] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [makeDefault, setMakeDefault] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const complete = policyCompleteness(spec).complete;
  useEffect(() => {
    if (step === 3 && !named) setName(suggestedName(spec));
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!t.can('policies.manage')) return <ErrorBox error={{ status: 403, message: tr("You don't have permission to do this.") }} />;
  const steps = [tr('What to back up'), tr('When'), tr('How long to keep'), tr('Name & computers')];
  const canNext = step !== 0 || complete;
  const create = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await post(`/api/t/${t.tenantId}/policies`, { name: name.trim() || suggestedName(spec), spec, deviceIds: [...picked], makeDefault });
      invalidatePlans(qc);
      toast(tr('Backup plan created'));
      nav(`${t.base}/plans/${r.id}`);
    } catch (e) {
      setErr(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <PageHead eyebrow={<Link to={`${t.base}/plans`}>{arrow.back()} {tr('Backup plans')}</Link>} title={tr('Create a backup plan')} desc={tr('Four short questions. The recommended answers are already chosen.')} />
      <div className="steps" aria-label={tr('Progress')}>
        {steps.map((s, i) => (
          <div key={s} className="row" style={{ gap: 0 }}>
            <div className={`st ${i === step ? 'on' : i < step ? 'done' : ''}`}><span className="n">{i < step ? '✓' : i + 1}</span>{s}</div>
            {i < steps.length - 1 && <span className="sep" />}
          </div>
        ))}
      </div>
      <div className="step-card">
        {step === 0 && <Card title={tr('What should be backed up?')}><WhatSection spec={spec} onChange={setSpec} previewable /></Card>}
        {step === 1 && <Card title={tr('When should backups run?')}><WhenSection spec={spec} onChange={setSpec} /></Card>}
        {step === 2 && <Card title={tr('How far back should you be able to go?')}><KeepSection spec={spec} onChange={setSpec} /></Card>}
        {step === 3 && (
          <div className="stack">
            <Card title={tr('Name the plan')}>
              <Field label={tr('Name')}><input type="text" value={name} maxLength={200} onChange={(e) => { setName(e.target.value); setNamed(true); }} /></Field>
              <PlanLines spec={spec} />
              <AdvancedSection spec={spec} onChange={setSpec} />
            </Card>
            <Card title={tr('Which computers should use this plan?')}>
              <ComputerPicker computers={computers.data ?? []} plans={plans.data?.policies ?? []} picked={picked} onChange={setPicked} target={spec} />
              <label className="check" style={{ marginTop: 16 }}>
                <input type="checkbox" checked={makeDefault} onChange={(e) => setMakeDefault(e.target.checked)} />
                <span>
                  <strong>{tr('Use this plan for new computers')}</strong>
                  <div className="small muted">{tr('New computers get this plan when they connect. Computers you already have keep their plans.')}</div>
                </span>
              </label>
            </Card>
            {err && <div className="notice bad">{err}</div>}
          </div>
        )}
        <div className="row between" style={{ marginTop: 16 }}>
          {step === 0 ? <Link className="btn" to={`${t.base}/plans`}>{tr('Cancel')}</Link> : <Button onClick={() => setStep(step - 1)}>{tr('Back')}</Button>}
          {step < 3 ? <Button variant="primary" disabled={!canNext} onClick={() => setStep(step + 1)}>{tr('Continue')}</Button> : <Button variant="primary" busy={busy} onClick={create}>{tr('Create plan')}</Button>}
        </div>
      </div>
    </div>
  );
}

/** Checkbox list of computers, showing each one's current plan and warning when history would get shorter. */
function ComputerPicker({ computers, plans, picked, onChange, target, exclude }: {
  computers: DeviceRow[];
  plans: PlanRow[];
  picked: Set<string>;
  onChange: (s: Set<string>) => void;
  target: PolicySpec;
  exclude?: string;
}) {
  const list = computers.filter((c) => c.policy_id !== exclude || !exclude);
  const planOf = (id: string | null) => plans.find((p) => p.id === id);
  const shorter = [...picked].filter((id) => {
    const cur = planOf(list.find((c) => c.id === id)?.policy_id ?? null);
    return cur && shortensHistory(cur.spec.retention, target.retention);
  });
  if (!list.length) return <p className="muted" style={{ margin: 0 }}>{exclude ? tr('All your computers already use this plan.') : tr("You haven't connected any computers yet. You can put them on this plan later.")}</p>;
  const toggle = (id: string, on: boolean) => {
    const n = new Set(picked);
    if (on) n.add(id);
    else n.delete(id);
    onChange(n);
  };
  return (
    <div className="stack" style={{ gap: 8 }}>
      <label className="check small muted">
        <input type="checkbox" checked={picked.size === list.length} onChange={(e) => onChange(e.target.checked ? new Set(list.map((c) => c.id)) : new Set())} />
        <span>{tr('Select all')}</span>
      </label>
      {list.map((c) => (
        <label key={c.id} className="check">
          <input type="checkbox" checked={picked.has(c.id)} onChange={(e) => toggle(c.id, e.target.checked)} />
          <span>
            <strong>{c.display_name}</strong>
            <span className="small muted"> · {c.policy_name ? tr('now on “{plan}”', { plan: c.policy_name }) : tr('no plan yet')}</span>
          </span>
        </label>
      ))}
      {shorter.length > 0 && (
        <div className="notice warn">
          {tr('{n} of the chosen computers now keep a longer history. With this plan, their backups older than {span} will be removed.', { n: shorter.length, span: spanText(historyDays(target.retention)) })}
        </div>
      )}
    </div>
  );
}

// ───────── one plan ─────────

export function PlanPage() {
  const t = useTenant();
  const { planId } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const manage = t.can('policies.manage');
  const q = useQuery({ queryKey: ['policy', planId], queryFn: () => get(`/api/t/${t.tenantId}/policies/${planId}`) });
  const onPlan = useQuery({
    queryKey: ['plan-devices', planId],
    queryFn: () => get<{ devices: DeviceRow[] }>(`/api/t/${t.tenantId}/devices?policyId=${planId}&limit=200`),
    select: (d) => d.devices.filter((x) => x.status !== 'retired'),
  });
  const plans = usePlans();
  const ten = useQuery({ queryKey: ['tenant', t.tenantId], queryFn: () => get(`/api/t/${t.tenantId}`) });
  const [spec, setSpec] = useState<PolicySpec | null>(null);
  const [name, setName] = useState('');
  const [formKey, setFormKey] = useState(0);
  const [dialog, setDialog] = useState<null | 'default' | 'pause' | 'delete' | 'add' | 'shorter' | { move: string }>(null);
  const [busy, setBusy] = useState(false);
  const policy = q.data?.policy;
  useEffect(() => {
    if (policy && !spec) {
      setSpec(policy.spec);
      setName(policy.name);
    }
  }, [policy]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = useMemo(() => !!policy && !!spec && (canonicalJson(spec) !== canonicalJson(policy.spec) || name !== policy.name), [policy, spec, name]);
  if (q.isLoading || (q.data && !spec)) return <Loading rows={8} />;
  if (q.error) return <ErrorBox error={q.error} />;
  if (!spec) return null;
  const computers = onPlan.data ?? [];
  const isDefault = !!q.data.usage.is_default;
  const complete = policyCompleteness(spec).complete;
  const reviewPending = isDefault && manage && t.can('settings.manage') && ten.data && !ten.data.tenant.settings?.planReviewed;
  const editable = manage && !policy.is_builtin;
  const refresh = () => invalidatePlans(qc);
  const markReviewed = async () => {
    await patch(`/api/t/${t.tenantId}/settings`, { planReviewed: true });
    qc.invalidateQueries({ queryKey: ['tenant'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  };
  const save = async () => {
    setBusy(true);
    try {
      await put(`/api/t/${t.tenantId}/policies/${planId}`, { name: name.trim() || policy.name, spec });
      if (reviewPending) await markReviewed();
      toast(tr('Changes saved. They apply from each computer’s next backup.'));
      refresh();
      const fresh = await q.refetch();
      if (fresh.data) {
        setSpec(fresh.data.policy.spec);
        setName(fresh.data.policy.name);
        setFormKey((k) => k + 1);
      }
      setDialog(null);
    } catch (e) {
      toast(friendlyError(e), true);
    } finally {
      setBusy(false);
    }
  };
  const trySave = () => (computers.length && shortensHistory(policy.spec.retention, spec.retention) ? setDialog('shorter') : save());
  const discard = () => {
    setSpec(policy.spec);
    setName(policy.name);
    setFormKey(formKey + 1);
  };
  return (
    <div>
      <PageHead
        eyebrow={<Link to={`${t.base}/plans`}>{arrow.back()} {tr('Backup plans')}</Link>}
        title={<>{policy.name} {isDefault && <span className="chip" style={{ verticalAlign: 'middle' }}>{tr('Default')}</span>}</>}
        desc={<>{usedBy(computers.length)}{isDefault && <> · {tr('New computers get this plan')}</>}</>}
        actions={editable ? (
          <>
            {!isDefault && q.data.policy.spec && policyCompleteness(policy.spec).complete && <Button onClick={() => setDialog('default')}>{tr('Make default')}</Button>}
            {policy.paused
              ? <Button onClick={async () => { await post(`/api/t/${t.tenantId}/policies/${planId}/resume`); refresh(); toast(tr('Backups resumed')); }}>{tr('Resume backups')}</Button>
              : <Button onClick={() => setDialog('pause')}>{tr('Pause backups')}</Button>}
            <Button onClick={async () => {
              try {
                const r = await post(`/api/t/${t.tenantId}/policies`, { name: tr('Copy of {name}', { name: policy.name }).slice(0, 200), spec: policy.spec });
                refresh();
                nav(`${t.base}/plans/${r.id}`);
              } catch (e) {
                toast(friendlyError(e), true);
              }
            }}>{tr('Duplicate')}</Button>
          </>
        ) : undefined}
      />
      {reviewPending && (
        <div className="notice" style={{ marginBottom: 16 }}>
          <strong>{tr('New computers get this plan. Does it back up the right things?')}</strong>
          <span>{tr('Check the choices below. Change anything you like, then save, or confirm that it looks good.')}</span>
          <div className="row" style={{ marginTop: 10 }}>
            <Button size="sm" variant="primary" onClick={async () => { await markReviewed(); toast(tr('Great, thanks!')); }}>{tr('Looks good')}</Button>
          </div>
        </div>
      )}
      {policy.paused && (
        <div className="notice warn" style={{ marginBottom: 16 }}>
          <strong>{tr('Backups are paused for the computers on this plan')}</strong>
          {tr('Existing backups are kept, but no new backups run until you resume.')}
        </div>
      )}
      {!complete && <div className="notice warn" style={{ marginBottom: 16 }}><strong>{tr('This plan is not finished')}</strong>{tr('Choose at least one folder to back up. Computers can only use the plan after that.')}</div>}
      <div key={formKey} className="stack">
        {editable && (
          <Card>
            <Field label={tr('Name of the plan')}><input type="text" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} style={{ maxWidth: 420 }} /></Field>
          </Card>
        )}
        <Card title={tr('What gets backed up')}><WhatSection spec={spec} onChange={setSpec} disabled={!editable} previewable={editable} /></Card>
        <div className="grid g2">
          <Card title={tr('When')}><WhenSection spec={spec} onChange={setSpec} disabled={!editable} /></Card>
          <Card title={tr('How long backups are kept')}><KeepSection spec={spec} onChange={setSpec} disabled={!editable} /></Card>
        </div>
        <AdvancedSection spec={spec} onChange={setSpec} disabled={!editable} />
        <Card
          title={<>{tr('Computers on this plan')} <HelpTip>{tr('Each computer follows exactly one plan. Moving a computer to another plan takes effect at its next backup.')}</HelpTip></>}
          actions={editable && complete ? <Button size="sm" onClick={() => setDialog('add')}>{tr('Add computers')}</Button> : undefined}
          pad={false}
        >
          {onPlan.isLoading ? <div className="card-pad"><Loading rows={2} /></div> : computers.length === 0 ? (
            <Empty glyph="⌂" title={tr('No computers use this plan yet')} />
          ) : (
            <table>
              <tbody>
                {computers.map((c) => {
                  const st = computerStatus(c);
                  return (
                    <tr key={c.id}>
                      <td><Link to={`${t.base}/devices/${c.id}`}><strong>{c.display_name}</strong></Link></td>
                      <td><Badge tone={st.tone}>{st.label}</Badge></td>
                      <td className="right">{manage && <Button size="sm" variant="ghost" onClick={() => setDialog({ move: c.id })}>{tr('Move to another plan…')}</Button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
        <TechDetails summary={tr('Change history')}>
          <ul className="small" style={{ margin: 0, paddingInlineStart: 18 }}>
            {q.data.revisions.map((r: { id: string; created_at: string; created_by_email: string | null }, i: number) => (
              <li key={r.id}>{i === q.data.revisions.length - 1 ? tr('Created by {who} · {time}', { who: r.created_by_email ?? tr('ZeroGap'), time: when(r.created_at) }) : tr('Changed by {who} · {time}', { who: r.created_by_email ?? tr('ZeroGap'), time: when(r.created_at) })}</li>
            ))}
          </ul>
        </TechDetails>
        {editable && (
          <div>
            {isDefault ? (
              <p className="small muted">{tr('The default plan can’t be deleted. To delete it, first make another plan the default.')}</p>
            ) : (
              <Button variant="danger" size="sm" onClick={() => setDialog('delete')}>{tr('Delete this plan…')}</Button>
            )}
          </div>
        )}
      </div>
      {dirty && editable && (
        <div className="savebar" role="region" aria-label={tr('Unsaved changes')}>
          <span>{tr('You have unsaved changes')}</span>
          <span className="spacer" />
          <Button size="sm" onClick={discard}>{tr('Discard')}</Button>
          <Button size="sm" variant="brass" busy={busy} disabled={!complete} onClick={trySave}>{tr('Save changes')}</Button>
        </div>
      )}

      {dialog === 'shorter' && (
        <ConfirmDialog title={tr('Keep less history?')} confirm={tr('Save and remove older backups')} danger onClose={() => setDialog(null)} onConfirm={save}>
          <p style={{ margin: 0 }}>{tr('This plan will keep about {span} of history instead of {before}.', { span: spanText(historyDays(spec.retention)), before: spanText(historyDays(policy.spec.retention)) })}</p>
          <p style={{ margin: 0 }}><strong>{tr('Backups older than that will be removed within a few hours, on all {n} computers on this plan. This can’t be undone.', { n: computers.length })}</strong></p>
        </ConfirmDialog>
      )}
      {dialog === 'default' && (
        <ConfirmDialog title={tr('Make “{name}” the default plan?', { name: policy.name })} confirm={tr('Make default')} onClose={() => setDialog(null)}
          onConfirm={async () => { await post(`/api/t/${t.tenantId}/policies/${planId}/set-default`); refresh(); setDialog(null); toast(tr('New computers will now get this plan')); }}>
          <p style={{ margin: 0 }}>{tr('New computers will get this plan when they connect.')}</p>
          <p style={{ margin: 0 }}>{tr('Computers you already have keep their current plans. To move them, use “Add computers”.')}</p>
        </ConfirmDialog>
      )}
      {dialog === 'pause' && (
        <ConfirmDialog title={tr('Pause backups?')} confirm={tr('Pause backups')} onClose={() => setDialog(null)}
          onConfirm={async () => { await post(`/api/t/${t.tenantId}/policies/${planId}/pause`); refresh(); setDialog(null); toast(tr('Backups paused')); }}>
          <p style={{ margin: 0 }}>{tr('The {n} computers on this plan will stop backing up until you resume. Their existing backups are kept.', { n: computers.length })}</p>
        </ConfirmDialog>
      )}
      {dialog === 'delete' && <DeletePlanDialog plan={{ id: policy.id, name: policy.name }} computers={computers} plans={plans.data?.policies ?? []} onClose={() => setDialog(null)} onDone={() => { refresh(); nav(`${t.base}/plans`); toast(tr('Plan deleted')); }} />}
      {dialog === 'add' && (
        <AddComputersDialog planId={policy.id} spec={spec} onClose={() => setDialog(null)} onDone={() => { setDialog(null); refresh(); toast(tr('Computers moved to this plan')); }} />
      )}
      {dialog && typeof dialog === 'object' && (
        <ChangePlanDialog deviceIds={[dialog.move]} currentPlanId={policy.id} onClose={() => setDialog(null)} onDone={() => { setDialog(null); refresh(); }} />
      )}
    </div>
  );
}

function AddComputersDialog({ planId, spec, onClose, onDone }: { planId: string; spec: PolicySpec; onClose: () => void; onDone: () => void }) {
  const t = useTenant();
  const computers = useComputers();
  const plans = usePlans();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <Modal title={tr('Add computers to this plan')} onClose={onClose} footer={
      <>
        <Button onClick={onClose}>{tr('Cancel')}</Button>
        <Button variant="primary" busy={busy} disabled={picked.size === 0} onClick={async () => {
          setBusy(true);
          setErr(null);
          try {
            await post(`/api/t/${t.tenantId}/policies/${planId}/assign`, { deviceIds: [...picked] });
            onDone();
          } catch (e) {
            setErr(friendlyError(e));
          } finally {
            setBusy(false);
          }
        }}>{tr('Move to this plan')}</Button>
      </>
    }>
      {computers.isLoading ? <Loading rows={3} /> : (
        <ComputerPicker computers={computers.data ?? []} plans={plans.data?.policies ?? []} picked={picked} onChange={setPicked} target={spec} exclude={planId} />
      )}
      {err && <div className="notice bad" style={{ marginTop: 12 }}>{err}</div>}
    </Modal>
  );
}

function DeletePlanDialog({ plan, computers, plans, onClose, onDone }: { plan: { id: string; name: string }; computers: DeviceRow[]; plans: PlanRow[]; onClose: () => void; onDone: () => void }) {
  const t = useTenant();
  const others = plans.filter((p) => p.id !== plan.id && p.complete);
  const [target, setTarget] = useState(others.find((p) => p.is_default)?.id ?? others[0]?.id ?? '');
  const blocked = computers.length > 0 && !target;
  return (
    <ConfirmDialog title={tr('Delete “{name}”?', { name: plan.name })} confirm={computers.length ? tr('Move computers and delete') : tr('Delete plan')} danger onClose={onClose}
      onConfirm={async () => {
        if (blocked) return;
        if (computers.length) await post(`/api/t/${t.tenantId}/devices/bulk`, { deviceIds: computers.map((c) => c.id), policyId: target });
        await del(`/api/t/${t.tenantId}/policies/${plan.id}`);
        onDone();
      }}>
      {computers.length === 0 ? (
        <p style={{ margin: 0 }}>{tr('No computers use this plan. Deleting it does not affect any backups.')}</p>
      ) : blocked ? (
        <p style={{ margin: 0 }}>{tr('{n} computers use this plan, and there is no other finished plan to move them to. Create another plan first.', { n: computers.length })}</p>
      ) : (
        <>
          <p style={{ margin: 0 }}>{tr('{n} computers use this plan. Choose the plan they should move to:', { n: computers.length })}</p>
          <select value={target} onChange={(e) => setTarget(e.target.value)}>{others.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
          <p className="small muted" style={{ margin: 0 }}>{tr('Their existing backups are kept. From now on they follow the plan you choose.')}</p>
        </>
      )}
    </ConfirmDialog>
  );
}

/** Choose the backup plan for one or more computers. */
export function ChangePlanDialog({ deviceIds, currentPlanId, onClose, onDone }: { deviceIds: string[]; currentPlanId?: string | null; onClose: () => void; onDone: () => void }) {
  const t = useTenant();
  const toast = useToast();
  const plans = usePlans();
  const ready = (plans.data?.policies ?? []).filter((p) => p.complete);
  const [choice, setChoice] = useState(currentPlanId ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const current = ready.find((p) => p.id === currentPlanId);
  const chosen = ready.find((p) => p.id === choice);
  const shorter = current && chosen && shortensHistory(current.spec.retention, chosen.spec.retention);
  const title = deviceIds.length === 1 ? tr('Change backup plan') : tr('Change backup plan for {n} computers', { n: deviceIds.length });
  return (
    <Modal wide title={title} onClose={onClose} footer={
      <>
        <Button onClick={onClose}>{tr('Cancel')}</Button>
        <Button variant="primary" busy={busy} disabled={!choice || choice === currentPlanId} onClick={async () => {
          setBusy(true);
          setErr(null);
          try {
            if (deviceIds.length === 1) await patch(`/api/t/${t.tenantId}/devices/${deviceIds[0]}`, { policyId: choice });
            else await post(`/api/t/${t.tenantId}/devices/bulk`, { deviceIds, policyId: choice });
            toast(tr('Backup plan changed. It applies from the next backup.'));
            onDone();
          } catch (e) {
            setErr(friendlyError(e));
          } finally {
            setBusy(false);
          }
        }}>{tr('Use this plan')}</Button>
      </>
    }>
      {plans.isLoading ? <Loading rows={3} /> : (
        <div className="optcards">
          {ready.map((p) => (
            <div key={p.id} className={`optcard ${choice === p.id ? 'on' : ''}`}>
              <label>
                <input type="radio" name="plan" checked={choice === p.id} onChange={() => setChoice(p.id)} />
                <span className="optcard-text">
                  <span className="optcard-title">{p.name} {p.is_default && <span className="chip">{tr('Default')}</span>} {p.id === currentPlanId && <span className="small muted">({tr('current')})</span>}</span>
                  <PlanLines spec={p.spec} />
                </span>
              </label>
            </div>
          ))}
        </div>
      )}
      {shorter && <div className="notice warn" style={{ marginTop: 12 }}>{tr('This plan keeps a shorter history. Backups older than {span} will be removed.', { span: spanText(historyDays(chosen!.spec.retention)) })}</div>}
      <p className="small muted">{tr('Need something different?')} <Link to={`${t.base}/plans/new`}>{tr('Create a backup plan')}</Link></p>
      {err && <div className="notice bad">{err}</div>}
    </Modal>
  );
}
